import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Tag, message } from 'antd';
import { useMutation } from '@apollo/client';
import { WorkbenchAlert } from '../types';
import {
  composeAlertHeadline,
  composeAlertLabel,
  composeAlertRecommendedAction,
  composeAlertRiskTag,
  composeAlertSeverity,
} from '../utils/alertTextComposer';
import { formatLocalTime } from '../utils/taskTextComposer';
import { MARK_WORKBENCH_ALERT_HANDLED } from '../graphql/mutations/workbenchMutations';
import NotifyDspButton from './NotifyDspButton';
import AlertEntityRow from './AlertEntityRow';

interface AlertCardProps {
  alert: WorkbenchAlert;
  warehouseId?: number;
  timeZone?: string | null;
  isHandled?: boolean;
  onHandled?: () => void;
}

const severityStyle = (severity: string): { border: string; bar: string; text: string; tag: string } => {
  switch (severity) {
    case 'HIGH':
      return { border: 'border-red-200', bar: 'bg-red-500', text: 'text-red-600', tag: 'red' };
    case 'MEDIUM':
      return { border: 'border-amber-200', bar: 'bg-amber-500', text: 'text-amber-600', tag: 'orange' };
    default:
      return { border: 'border-gray-200', bar: 'bg-gray-400', text: 'text-gray-600', tag: 'default' };
  }
};

const AlertCard: React.FC<AlertCardProps> = ({ alert, warehouseId, timeZone, isHandled, onHandled }) => {
  const { t } = useTranslation();
  const style = severityStyle(composeAlertSeverity(alert));
  const [markHandled, { loading }] = useMutation(MARK_WORKBENCH_ALERT_HANDLED);
  // 处置说明选填，但仍要显式点「提交」：留言会随 completeTaskManually 的 comment 落到上游任务上，
  // 与片区日报共用同一份处理证据，误触即写库不可撤销
  const [commenting, setCommenting] = useState(false);
  const [draft, setDraft] = useState('');

  const handleMarkHandled = async () => {
    if (!warehouseId) return;
    try {
      const { data } = await markHandled({
        variables: {
          warehouseId,
          alertId: alert.alertId,
          actionCode: null,
          actionTaken: draft.trim() || null,
        },
      });
      // 上游对重复提交做状态守卫返回 false，这不是错误，提示「已处理」而非报错
      if (data?.markWorkbenchAlertHandled) {
        message.success(String(t('workbench.markedHandled')));
      } else {
        message.info(String(t('workbench.alertText.alreadyHandled')));
      }
      onHandled?.();
    } catch (e) {
      message.error(String(t('workbench.alertText.handleFailed')));
    }
  };

  const label = composeAlertLabel(alert);
  const headline = composeAlertHeadline(alert);
  const riskTag = composeAlertRiskTag(alert);
  const recommendedAction = composeAlertRecommendedAction(alert);

  // 实时告警是 DSP 维度卡，dspId 可缺省由上游按任务维度取；卡上恰有一个 DSP 明细时顺带传上去，
  // 多个 DSP 说明这张卡不是单 DSP 维度，交给上游判断比我方挑一个更安全
  const dspEntities = alert.entities.filter((entity) => entity.entityType === 'DSP');
  const singleDsp = dspEntities.length === 1 ? dspEntities[0] : undefined;

  // 不能用 overflow-hidden 裁色条圆角：它会把明细行的宽度需求截断，页面就不横滚了。
  // 改由色条自己贴合左侧圆角
  return (
    <div className={`relative border rounded-lg pl-4 pr-3 py-3 ${style.border}`}>
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${style.bar}`} />

      <div className="flex items-start justify-between gap-2">
        <p className={`text-sm font-semibold min-w-0 ${style.text}`}>
          {label}
        </p>
        {riskTag && (
          <Tag color={style.tag} className="shrink-0 m-0">
            {riskTag}
          </Tag>
        )}
      </div>

      {headline && <p className="text-sm font-semibold text-gray-900 mt-2">{headline}</p>}

      {alert.contextText && (
        <p className="text-xs text-gray-500 mt-1">{alert.contextText}</p>
      )}

      {/* 明细行（滞留线路 / 落后司机）：与周期复盘同为上游 TaskDetailDTO，共用 AlertEntityRow。
          「联系 DSP」不挂在行上——上游按 taskId 就会给该任务下所有 DSP 发消息，按钮统一放卡片底部 */}
      {alert.entities.length > 0 && (
        <div className="mt-2 divide-y divide-red-100">
          {alert.entities.map((entity) => (
            <AlertEntityRow
              key={`${entity.entityType}-${entity.entityId}`}
              alert={alert}
              entity={entity}
            />
          ))}
        </div>
      )}

      {recommendedAction && (
        <p className="text-xs text-gray-600 mt-2">{recommendedAction}</p>
      )}

      {isHandled ? (
        alert.comment && (
          <div className="mt-2 border-l-2 border-violet-200 pl-3">
            <p className="text-sm text-gray-600 italic">“{alert.comment.content}”</p>
            <p className="text-xs text-gray-400 mt-1">
              {alert.comment.byName} {formatLocalTime(alert.comment.at, timeZone) || '—'}
            </p>
          </div>
        )
      ) : (
        <>
          {commenting && (
            <Input.TextArea
              className="mt-3"
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={String(t('workbench.addCommentPlaceholder'))}
            />
          )}

          <div className="flex items-center gap-2 mt-3">
            {commenting ? (
              <>
                <Button
                  size="small"
                  type="primary"
                  loading={loading}
                  disabled={!warehouseId}
                  onClick={handleMarkHandled}
                >
                  {String(t('workbench.markHandled'))}
                </Button>
                <Button
                  size="small"
                  disabled={loading}
                  onClick={() => {
                    setCommenting(false);
                    setDraft('');
                  }}
                >
                  {String(t('common.cancel'))}
                </Button>
              </>
            ) : (
              <>
                {/* 「联系 DSP」是设计稿的主动作：告警的首选处置是把问题推给 DSP，
                    「我已处理」是仓管自行消化后的登记，故排在其后用次级样式 */}
                <NotifyDspButton
                  warehouseId={warehouseId}
                  alertId={alert.alertId}
                  dspId={singleDsp?.entityId}
                  dspName={singleDsp?.entityName}
                  onNotified={onHandled}
                />
                <Button size="small" disabled={!warehouseId} onClick={() => setCommenting(true)}>
                  {String(t('workbench.markHandled'))}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AlertCard;
